import { Badge } from "react-bootstrap";
import { InvitationStatus } from "@features/invitations/types/invitation";
import { subtleBadgeProps } from "@core/utils/subtleBadge";

/**
 * Renders a badge displaying the status of an invitation.
 *
 * @param status - The current status of the invitation.
 *   Supported values are:
 *   - "pending": Invitation is awaiting response.
 *   - "accepted": Invitation has been accepted.
 *   - "revoked": Invitation has been revoked.
 *   - "declined": Invitation has been declined.
 *   - "all": Represents all statuses.
 *
 * Displays a colored badge with a label corresponding to the status.
 */
const StatusBadge: React.FC<{ status: InvitationStatus }> = ({ status }) => {
    // Map each status to its corresponding text and background color.
    const map: Record<InvitationStatus, { text: string; bg: string }> = {
        pending: { text: "Pending", bg: "warning" },
        accepted: { text: "Accepted", bg: "success" },
        revoked: { text: "Revoked", bg: "dark" },
        declined: { text: "Declined", bg: "danger" },
        all: { text: "All", bg: "light" },
    };

    // Fallback to "Unknown" if the status is not recognized.
    const { text, bg } = map[status] || { text: "Unknown", bg: "secondary" };

    return (
        <Badge {...subtleBadgeProps(bg)} data-testid="status-badge">{text}</Badge>
    );
}

export default StatusBadge;